import { Controller } from "@hotwired/stimulus";
import Cleave from "cleave.js";
import "cleave.js/dist/addons/cleave-phone.us";

// Connects to data-controller="payment"
export default class extends Controller {
  static targets = ["cardNumber", "expDate", "cvc", "cardName"];
  static values = {
    clientKey: String,
    apiLoginId: String,
    acceptjsUrl: String,
  };

  connect() {
    // Ensure overlay is hidden when controller is connected (handles back button case)
    this.hideOverlay();
    this.ensureAcceptJsLoaded();
    if (this.hasCardNumberTarget) {
      this.cardNumberCleave = new Cleave(this.cardNumberTarget, {
        creditCard: true,
      });
      this.cardNumberTarget.addEventListener("blur", this.validateCardNumber);
      this.cardNumberTarget.addEventListener("change", this.validateCardNumber);
    }
    if (this.hasExpDateTarget) {
      this.expDateCleave = new Cleave(this.expDateTarget, {
        date: true,
        datePattern: ["m", "y"],
      });
      this.expDateTarget.addEventListener("blur", this.validateExpDate);
      this.expDateTarget.addEventListener("change", this.validateExpDate);
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.addEventListener("blur", this.validateCvc);
      this.cvcTarget.addEventListener("change", this.validateCvc);
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.addEventListener("blur", this.validateCardName);
      this.cardNameTarget.addEventListener("change", this.validateCardName);
    }
    this.element.addEventListener("submit", this.handleSubmit);
  }

  disconnect() {
    if (this.cardNumberCleave) {
      this.cardNumberCleave.destroy();
      this.cardNumberTarget.removeEventListener(
        "blur",
        this.validateCardNumber,
      );
      this.cardNumberTarget.removeEventListener(
        "change",
        this.validateCardNumber,
      );
    }
    if (this.expDateCleave) {
      this.expDateCleave.destroy();
      this.expDateTarget.removeEventListener("blur", this.validateExpDate);
      this.expDateTarget.removeEventListener("change", this.validateExpDate);
    }
    if (this.hasCvcTarget) {
      this.cvcTarget.removeEventListener("blur", this.validateCvc);
      this.cvcTarget.removeEventListener("change", this.validateCvc);
    }
    if (this.hasCardNameTarget) {
      this.cardNameTarget.removeEventListener("blur", this.validateCardName);
      this.cardNameTarget.removeEventListener("change", this.validateCardName);
    }
    this.element.removeEventListener("submit", this.handleSubmit);
  }

  showOverlay() {
    const overlay = document.getElementById("payment-overlay");
    if (overlay) {
      overlay.classList.remove("d-none");
      document.body.classList.add("no-freeze-spinner");
    }
  }

  hideOverlay() {
    const overlay = document.getElementById("payment-overlay");
    if (overlay) {
      overlay.classList.add("d-none");
      document.body.classList.remove("no-freeze-spinner");
    }
  }

  handleSubmit = (e) => {
    e.preventDefault(); // Always prevent default form submission

    this.showOverlay();

    // 1. Validate fields
    const validCardNumber = this.validateCardNumber();
    const validExpDate = this.validateExpDate();
    const validCvc = this.validateCvc();
    const validCardName = this.validateCardName();

    if (!(validCardNumber && validExpDate && validCvc && validCardName)) {
      // Focus first invalid field
      const firstInvalid = [
        this.cardNumberTarget,
        this.expDateTarget,
        this.cvcTarget,
        this.cardNameTarget,
      ].find((input) => input && input.classList.contains("is-invalid"));
      if (firstInvalid) firstInvalid.focus();
      this.hideOverlay();
      return;
    }

    // 2. Prepare data for Accept.js
    const cardNumber = this.cardNumberTarget.value.replace(/\s+/g, "");
    const exp = this.expDateTarget.value.split("/");
    const expMonth = exp[0].trim();
    let expYear = exp[1].trim();
    if (expYear.length === 2) expYear = "20" + expYear;
    const cardCode = this.cvcTarget.value.trim();

    // 3. Accept.js credentials (replace with your real values)
    const authData = {
      clientKey: this.clientKeyValue,
      apiLoginID: this.apiLoginIdValue,
    };

    const cardData = {
      cardNumber: cardNumber,
      month: expMonth,
      year: expYear,
      cardCode: cardCode,
    };

    const secureData = {
      authData: authData,
      cardData: cardData,
    };

    Accept.dispatchData(secureData, (response) => {
      if (response.messages.resultCode === "Ok") {
        // Fill hidden fields with opaqueData
        const opaqueData = response.opaqueData;
        document.getElementById("opaqueDataDescriptor").value =
          opaqueData.dataDescriptor;
        document.getElementById("opaqueDataValue").value = opaqueData.dataValue;
        // Submit the form
        this.element.submit();
      } else {
        this.hideOverlay();
        // Show Accept.js error
        const errorMsg = response.messages.message[0].text;
        let errorDiv = document.getElementById("payment-errors");
        if (errorDiv) {
          errorDiv.textContent = errorMsg;
        } else {
          alert(errorMsg);
        }
      }
    });
  };

  validateCardNumber = () => {
    if (!this.hasCardNumberTarget) return true;
    const value = this.cardNumberTarget.value.replace(/\s+/g, "");
    let valid = value.length >= 13 && value.length <= 19 && /^\d+$/.test(value);
    this.setFieldValidity(
      this.cardNumberTarget,
      "card-number-feedback",
      valid,
      valid ? "" : "Card number is invalid.",
    );
    return valid;
  };

  validateExpDate = () => {
    if (!this.hasExpDateTarget) return true;
    const value = this.expDateTarget.value.trim();
    const match = value.match(/^(\d{2})\/(\d{2,4})$/);
    let valid = false;
    let msg = "";
    if (!match) {
      msg = "Expiration date format is invalid.";
    } else {
      let [_, mm, yy] = match;
      mm = parseInt(mm, 10);
      yy = parseInt(yy, 10);
      if (yy < 100) {
        yy += 2000;
      }
      const now = new Date();
      const thisMonth = now.getMonth() + 1;
      const thisYear = now.getFullYear();
      if (
        mm < 1 ||
        mm > 12 ||
        yy < thisYear ||
        (yy === thisYear && mm < thisMonth)
      ) {
        msg = "Expiration date is in the past.";
      } else {
        valid = true;
      }
    }
    this.setFieldValidity(this.expDateTarget, "exp-date-feedback", valid, msg);
    return valid;
  };

  validateCvc = () => {
    if (!this.hasCvcTarget) return true;
    const value = this.cvcTarget.value.trim();
    let valid = /^\d{3,4}$/.test(value);
    this.setFieldValidity(
      this.cvcTarget,
      "cvc-feedback",
      valid,
      valid ? "" : "CVV is invalid.",
    );
    return valid;
  };

  validateCardName = () => {
    if (!this.hasCardNameTarget) return true;
    const value = this.cardNameTarget.value.trim();
    let valid = value.length > 0;
    this.setFieldValidity(
      this.cardNameTarget,
      "card-name-feedback",
      valid,
      valid ? "" : "Name on Card is required.",
    );
    return valid;
  };

  setFieldValidity(input, feedbackId, valid, msg) {
    let feedback = document.getElementById(feedbackId);
    if (!feedback) {
      // Dynamically create the feedback div if it doesn't exist
      feedback = document.createElement("div");
      feedback.id = feedbackId;
      feedback.className = "invalid-feedback";
      // Insert after the input
      if (input.nextSibling) {
        input.parentNode.insertBefore(feedback, input.nextSibling);
      } else {
        input.parentNode.appendChild(feedback);
      }
    }
    if (input && feedback) {
      if (!valid) {
        input.classList.add("is-invalid");
        feedback.textContent = msg;
      } else {
        input.classList.remove("is-invalid");
        feedback.textContent = "";
      }
    }
  }

  ensureAcceptJsLoaded() {
    if (
      typeof window.Accept === "undefined" &&
      !document.getElementById("acceptjs-script")
    ) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = this.acceptjsUrlValue;
      script.id = "acceptjs-script";
      document.head.appendChild(script);
    }
  }
}

